import axios from 'axios'
import qs from 'qs'
import request from '../helpers/authorizateRequest'
import { contractError, TIMELINE_KEY } from './operatorMonitoringContract'

const BASE_URL = '/operator-monitoring/v1'

// With `responseType: 'blob'` the interceptor rejects with the error body as a
// Blob; read it back into the usual `{ code, message }` object.
function rethrowBlobError(error) {
    if (typeof Blob === 'undefined' || !(error instanceof Blob) || typeof error.text !== 'function') throw error
    return error.text().then(text => {
        let parsed = null
        try { parsed = JSON.parse(text) } catch (_) { /* not a JSON body */ }
        const isObject = parsed && Object.prototype.toString.call(parsed) === '[object Object]'
        throw isObject ? parsed : error
    })
}

function positiveId(value, name) {
    const id = Number(value)
    if (!Number.isInteger(id) || id < 1 || String(value).trim() !== String(id)) {
        throw contractError('invalid_request_resource_id', `${name} должен быть положительным целым числом`)
    }
    return id
}

function timelineParams(params = {}) {
    const normalized = { ...params }
    if (normalized.limit !== undefined) {
        const limit = Number(normalized.limit)
        if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
            throw contractError('invalid_timeline_limit', 'limit должен быть целым числом от 1 до 100')
        }
        normalized.limit = limit
    }

    const hasFrom = Boolean(normalized.range_from_key)
    const hasTo = Boolean(normalized.range_to_key)
    if (hasFrom !== hasTo || (normalized.before_cursor && hasFrom)) {
        throw contractError(
            'invalid_timeline_boundary',
            'Границы range должны передаваться парой и без before_cursor'
        )
    }
    if (hasFrom && (!TIMELINE_KEY.test(normalized.range_from_key) || !TIMELINE_KEY.test(normalized.range_to_key))) {
        throw contractError('invalid_timeline_key', 'Получен недопустимый ключ timeline')
    }
    return normalized
}

function timelineKeys(keys) {
    if (!Array.isArray(keys)) {
        throw contractError('invalid_timeline_keys', 'keys должен быть массивом timeline-ключей')
    }
    const normalized = [...new Set(keys)]
    if (!normalized.length || normalized.length > 100 || normalized.some(key => !TIMELINE_KEY.test(key))) {
        throw contractError('invalid_timeline_keys', 'Нужно передать от 1 до 100 корректных timeline-ключей')
    }
    return normalized
}

function validateAttachmentResponse(response) {
    const rawContentType = response && response.headers && response.headers['content-type']
    const contentType = String(rawContentType || '').split(';', 1)[0].trim().toLowerCase()
    const data = response && response.data
    const isStructuredPayload = data && Object.prototype.toString.call(data) === '[object Object]'

    if (!response || !data || !contentType || contentType.includes('json') || isStructuredPayload) {
        throw contractError('invalid_attachment_response', 'Сервер вернул некорректный бинарный ответ вложения')
    }
    return response
}

function get(path, params = {}, cancelToken = null) {
    return request.get(`${BASE_URL}${path}`, {
        params,
        cancelToken,
    }).then(response => response.data)
}

export default {
    createCancelSource() {
        return axios.CancelToken.source()
    },

    isCancel(error) {
        return axios.isCancel(error)
    },

    reference(cancelToken) {
        return get('/reference', {}, cancelToken)
    },

    operators(params, cancelToken) {
        return get('/operators', params, cancelToken)
    },

    operator(operatorId, cancelToken) {
        return get(`/operators/${positiveId(operatorId, 'operator_id')}`, {}, cancelToken)
    },

    operatorChats(operatorId, params, cancelToken) {
        return get(`/operators/${positiveId(operatorId, 'operator_id')}/chats`, params, cancelToken)
    },

    chat(chatId, cancelToken) {
        return get(`/chats/${positiveId(chatId, 'chat_id')}`, {}, cancelToken)
    },

    chatMessages(chatId, params, cancelToken) {
        return get(`/chats/${positiveId(chatId, 'chat_id')}/messages`, timelineParams(params), cancelToken)
    },

    chatItems(chatId, keys, cancelToken) {
        const normalizedChatId = positiveId(chatId, 'chat_id')
        return request.get(`${BASE_URL}/chats/${normalizedChatId}/items`, {
            params: { keys: timelineKeys(keys) },
            paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
            cancelToken,
        }).then(response => response.data)
    },

    downloadAttachment(chatId, sourceMessageId, cancelToken) {
        const normalizedChatId = positiveId(chatId, 'chat_id')
        const normalizedMessageId = positiveId(sourceMessageId, 'source_message_id')
        return request.get(
            `${BASE_URL}/chats/${normalizedChatId}/messages/${normalizedMessageId}/file`,
            {
                cancelToken,
                responseType: 'blob',
            }
        ).then(validateAttachmentResponse, rethrowBlobError)
    },
}
